import React, { FC, useEffect, useState } from 'react';
import { CheckOutlined } from '@ant-design/icons';
import { Col, Row } from 'antd';
import { api, endpoints } from '../../../../api';
import {
  DocumentSvg,
  ExpertiseAppointedSvg,
  ExpertiseComplete,
  ExpertiseInWorkSvg,
  ExpertiseNotStartedSvg,
  ExpertiseReadySvg,
} from '../../../../icons';
import { ColoredSmallText, CustomFormLayout, FormItem, GridItem } from '../../../../styles/styled';
import { StatusType } from '../../../ApplicationPage/types';
import { LanguagesType, PublishersType } from '../../../NewApplicationPage/types';
import { ExpertType } from '../../../NewExpertPage/types';
import { ExpertProfileContainer } from './styled';

type ExpertProfileProps = {
  expert: ExpertType | null;
};

export const ExpertiseStatusSvgGenerator = (status: StatusType) => {
  switch (status.id) {
    case 1:
      return <ExpertiseAppointedSvg />;
    case 2:
      return <ExpertiseReadySvg />;
    case 3:
      return <ExpertiseReadySvg />;
    case 4:
      return <ExpertiseNotStartedSvg />;
    case 5:
      return <ExpertiseInWorkSvg />;
    case 6:
      return <ExpertiseComplete />;
  }
};

function declOfNum(number: number | undefined, words: string[]) {
  return typeof number === 'number'
    ? words[number % 100 > 4 && number % 100 < 20 ? 2 : [2, 0, 1, 1, 1, 2][number % 10 < 5 ? Math.abs(number) % 10 : 5]]
    : '';
}

export const ExpertProfileComponent: FC<ExpertProfileProps> = ({ expert }) => {
  const [languagesState, setLanguagesState] = useState<LanguagesType>([]);
  const [publishersState, setPublishers] = useState<PublishersType>();

  useEffect(() => {
    (async () => {
      const languagesResponse = await api.get<LanguagesType>(endpoints.getLanguages());
      const publishersResponse = await api.get<PublishersType>(endpoints.getPublishers(0, 1000));

      setLanguagesState(languagesResponse.data);
      setPublishers(publishersResponse.data);
    })();
  }, []);

  return (
    <ExpertProfileContainer>
      <Row gutter={[15, 15]}>
        <Col span={24}>
          <CustomFormLayout type="light" color="#378dda" size="18px" padding="0">
            Контактная информация
          </CustomFormLayout>
        </Col>
        <Col span={8}>
          <FormItem>
            <ColoredSmallText>Телефон</ColoredSmallText>
            <ColoredSmallText color="rgba(56,72,91,.8)">{expert?.phone}</ColoredSmallText>
          </FormItem>
        </Col>
        <Col span={8}>
          <FormItem>
            <ColoredSmallText>E-mail</ColoredSmallText>
            <ColoredSmallText color="rgba(56,72,91,.8)">{expert?.email}</ColoredSmallText>
          </FormItem>
        </Col>
        <Col span={8}>
          <FormItem>
            <ColoredSmallText> Дата рождения</ColoredSmallText>
            <ColoredSmallText color="rgba(56,72,91,.8)">
              {expert?.birthday
                ? expert?.birthday?.includes('-')
                  ? expert.birthday.split('-').reverse().join('.')
                  : expert?.birthday
                : '-'}
            </ColoredSmallText>
          </FormItem>
        </Col>
        <Col span={8}>
          <FormItem>
            <ColoredSmallText>Адрес фактического проживания</ColoredSmallText>
            <ColoredSmallText color="rgba(56,72,91,.8)">
              {expert?.address ? expert?.address : 'отсутствует'}
            </ColoredSmallText>
          </FormItem>
        </Col>

        <Col span={8}>
          <FormItem>
            <ColoredSmallText>Регион</ColoredSmallText>
            <ColoredSmallText color="rgba(56,72,91,.8)">{expert?.region?.title || 'отсутствует'}</ColoredSmallText>
          </FormItem>
        </Col>

        <Col span={16} />
        {expert?.expert_educational_institutions && expert?.expert_educational_institutions?.length > 0 && (
          <Col span={24}>
            <CustomFormLayout type="light" color="#378dda" size="18px" padding="0">
              Сведения об образовании
            </CustomFormLayout>
          </Col>
        )}
        <Col span={24}>
          {expert?.expert_educational_institutions?.map((el, index, row) => {
            return (
              <Row key={el.id} gutter={[15, 15]}>
                <Col span={1}>
                  <ColoredSmallText color="rgba(56,72,91,.8)" size="16px">
                    {index + 1}.{' '}
                  </ColoredSmallText>
                </Col>
                <Col span={23}>
                  <ColoredSmallText color="rgba(56,72,91,.8)" size="16px">
                    {el.name}
                  </ColoredSmallText>
                </Col>
                <Col span={1} />
                <Col span={10}>
                  <FormItem>
                    <ColoredSmallText>Специальность</ColoredSmallText>
                    <ColoredSmallText color="rgba(56,72,91,.8)">{el?.qualification || 'отсутствует'}</ColoredSmallText>
                  </FormItem>
                </Col>
                <Col span={6}>
                  <FormItem>
                    <ColoredSmallText>Год окончания</ColoredSmallText>
                    <ColoredSmallText color="rgba(56,72,91,.8)">{el?.year || 'отсутствует'}</ColoredSmallText>
                  </FormItem>
                </Col>
                <Col span={7}>
                  <FormItem>
                    <ColoredSmallText>№ диплома</ColoredSmallText>
                    <ColoredSmallText color="rgba(56,72,91,.8)">{el?.diplom_number || 'отсутствует'}</ColoredSmallText>
                  </FormItem>
                </Col>
                <Col span={1} />
                <Col span={10}>
                  <FormItem>
                    <ColoredSmallText>Специализация</ColoredSmallText>
                    <ColoredSmallText color="rgba(56,72,91,.8)">{el?.specialization || 'отсутствует'}</ColoredSmallText>
                  </FormItem>
                </Col>
                <Col span={6}>
                  <FormItem>
                    <ColoredSmallText>Направление подготовки</ColoredSmallText>
                    <ColoredSmallText color="rgba(56,72,91,.8)">
                      {el?.direction_of_preparation || 'отсутствует'}
                    </ColoredSmallText>
                  </FormItem>
                </Col>
                <Col span={7}>
                  <FormItem>
                    <ColoredSmallText>Профиль</ColoredSmallText>
                    <ColoredSmallText color="rgba(56,72,91,.8)">{el?.profile || 'отсутствует'}</ColoredSmallText>
                  </FormItem>
                </Col>

                <Col span={1} />
                <Col span={10}>
                  <FormItem>
                    <ColoredSmallText>Квалификация</ColoredSmallText>
                    <ColoredSmallText color="rgba(56,72,91,.8)">{el?.qualification || 'отсутствует'}</ColoredSmallText>
                  </FormItem>
                </Col>
                <Col span={7}>
                  <FormItem>
                    <ColoredSmallText>Специальность</ColoredSmallText>
                    <ColoredSmallText color="rgba(56,72,91,.8)">{el?.specialty || 'отсутствует'}</ColoredSmallText>
                  </FormItem>
                </Col>

                <Col span={24}>
                  <br />
                </Col>
                <Col span={1} />
                {el?.file && (
                  <Col span={23}>
                    <GridItem gap="20px" pointed align="flex-start" direction="row">
                      <DocumentSvg />
                      <ColoredSmallText underline color="rgba(56,72,91,.8)">
                        <a href={el.file.file_path} target="_blank" rel="noreferrer">
                          смотреть
                        </a>
                      </ColoredSmallText>
                      <ColoredSmallText underline color="rgba(56,72,91,.8)">
                        <a href={el.file.file_path} download>
                          скачать
                        </a>
                      </ColoredSmallText>
                    </GridItem>
                  </Col>
                )}
                {index !== row.length - 1 && (
                  <Col span={24}>
                    <br />
                    <br />
                  </Col>
                )}
              </Row>
            );
          })}
        </Col>

        <Col span={24} />

        {expert?.expert_degrees?.map((el, index) => {
          return (
            <Col key={index} span={24}>
              <Row gutter={[25, 25]}>
                <Col span={24}>
                  <CustomFormLayout type="light" color="#378dda" size="18px" padding="0">
                    Учёная степень
                  </CustomFormLayout>
                </Col>
                <Col span={24}>
                  <ColoredSmallText size="16px">Учёная степень</ColoredSmallText>
                  <ColoredSmallText underline color="rgba(56,72,91,.8)">
                    {el?.academic_degree?.title}
                  </ColoredSmallText>
                </Col>
                <Col span={24}>
                  <ColoredSmallText size="16px">Отрасль науки</ColoredSmallText>
                  <ColoredSmallText underline color="rgba(56,72,91,.8)">
                    {el?.branch_of_science?.title}
                  </ColoredSmallText>
                </Col>
                {el?.file && (
                  <Col span={23}>
                    <GridItem gap="20px" pointed align="flex-start" direction="row">
                      <DocumentSvg />
                      <ColoredSmallText underline color="rgba(56,72,91,.8)">
                        <a href={el.file.file_path} target="_blank" rel="noreferrer">
                          смотреть
                        </a>
                      </ColoredSmallText>
                      <ColoredSmallText underline color="rgba(56,72,91,.8)">
                        <a href={el.file.file_path} download>
                          скачать
                        </a>
                      </ColoredSmallText>
                    </GridItem>
                  </Col>
                )}
              </Row>
            </Col>
          );
        })}
        {expert?.expert_scientific_titles?.map((el, idx) => (
          <Col key={idx} span={24}>
            <Row gutter={[25, 25]}>
              <Col span={24}>
                <CustomFormLayout type="light" color="#378dda" size="18px" padding="0">
                  Учёное звание
                </CustomFormLayout>
              </Col>
              <Col span={24}>
                <GridItem align="flex-start">
                  <ColoredSmallText size="16px">Научное звание по специальности</ColoredSmallText>
                  <ColoredSmallText underline color="rgba(56,72,91,.8)">
                    {el.scientific_title?.title}
                  </ColoredSmallText>
                </GridItem>
              </Col>
              <Col span={24}>
                <GridItem align="flex-start">
                  <ColoredSmallText size="16px">Специальность</ColoredSmallText>
                  <ColoredSmallText underline color="rgba(56,72,91,.8)">
                    {el.specialty?.title || 'отсутствует'}
                  </ColoredSmallText>
                </GridItem>
              </Col>
              {el.file && (
                <Col span={23}>
                  <GridItem gap="20px" pointed align="flex-start" direction="row">
                    <DocumentSvg />
                    <ColoredSmallText underline color="rgba(56,72,91,.8)">
                      <a href={el.file?.file_path} target="_blank" rel="noreferrer">
                        смотреть
                      </a>
                    </ColoredSmallText>
                    <ColoredSmallText underline color="rgba(56,72,91,.8)">
                      <a href={el.file?.file_path} download>
                        скачать
                      </a>
                    </ColoredSmallText>
                  </GridItem>
                </Col>
              )}
            </Row>
          </Col>
        ))}

        <Col span={24}>
          <CustomFormLayout type="light" color="#378dda" size="18px" padding="0">
            Место работы
          </CustomFormLayout>
        </Col>
        <Col span={24}>
          <GridItem align="flex-start">
            <ColoredSmallText size="16px">Наименование организации</ColoredSmallText>
            <ColoredSmallText color="rgba(56,72,91,.8)" size="14px">
              {expert?.name_of_organization}
            </ColoredSmallText>
          </GridItem>
        </Col>
        <Col span={24}>
          <GridItem align="flex-start">
            <ColoredSmallText size="16px">Занимаемая должность</ColoredSmallText>
            <ColoredSmallText color="rgba(56,72,91,.8)" size="14px">
              {expert?.position}
            </ColoredSmallText>
          </GridItem>
        </Col>
        <Col span={12}>
          <GridItem align="flex-start">
            <CustomFormLayout type="light" color="#378dda" size="18px" padding="0">
              Предметная область
            </CustomFormLayout>
            <ColoredSmallText color="rgba(56,72,91,.8)" size="14px">
              {expert?.expert_teaching_experiences &&
                expert.expert_teaching_experiences.map((el) => el?.subject_area_general?.title).join(', ')}
            </ColoredSmallText>
          </GridItem>
        </Col>
        <Col span={12}>
          <GridItem align="flex-start">
            <CustomFormLayout type="light" color="#378dda" size="18px" padding="0">
              Предметы
            </CustomFormLayout>
            <ColoredSmallText color="rgba(56,72,91,.8)" size="14px">
              {expert?.expert_teaching_experiences
                ?.map((el) => el.subjects_general.map((e) => e.title))
                .flat()
                .join(', ')}
            </ColoredSmallText>

            {/*{expert?.expert_teaching_experiences &&*/}
            {/*  expert.expert_teaching_experiences[0]?.subjects_general.map((el, id, array) => (*/}
            {/*    <ColoredSmallText color="rgba(56,72,91,.8)" size="14px" key={el.id}>*/}
            {/*      {el?.title}*/}
            {/*      {id !== array.length - 1 && ','}*/}
            {/*    </ColoredSmallText>*/}
            {/*  ))}*/}
          </GridItem>
        </Col>
        <Col span={24}>
          <GridItem align="flex-start">
            <CustomFormLayout type="light" color="#378dda" size="18px" padding="0">
              Стаж педагогической деятельности
            </CustomFormLayout>
            <ColoredSmallText color="rgba(56,72,91,.8)" size="14px">
              {expert?.experience} {declOfNum(expert?.experience, ['год', 'года', 'лет'])}
            </ColoredSmallText>
          </GridItem>
        </Col>
        <Col span={24}>
          <GridItem align="flex-start">
            <CustomFormLayout type="light" color="#378dda" size="18px" padding="0">
              Педагогический опыт реализации основных образовательных программ
            </CustomFormLayout>
            <ColoredSmallText color="rgba(56,72,91,.8)" size="14px">
              {expert?.experience_of_implementation}{' '}
              {declOfNum(expert?.experience_of_implementation, ['год', 'года', 'лет'])}
            </ColoredSmallText>
          </GridItem>
        </Col>
        <Col span={24}>
          <GridItem align="flex-start">
            {expert?.native_languages && expert?.native_languages.length > 0 && (
              <CustomFormLayout type="light" color="#378dda" size="18px" padding="0">
                Знание родных языков
              </CustomFormLayout>
            )}
            {expert?.native_languages?.map((el) => (
              <ColoredSmallText key={el.id} color="rgba(56,72,91,.8)" size="14px">
                <div className="circle" />
                {languagesState.find((e) => e.id === el.id)?.title}
              </ColoredSmallText>
            ))}
          </GridItem>
        </Col>
        {/* <CheckOutlined/> */}
        <Col span={24}>
          {expert?.educational_law_awareness && (
            <GridItem align="center" justify="flex-start" direction="row" wrap="nowrap" gap="10px">
              <CheckOutlined style={{ color: 'blue' }} />
              <ColoredSmallText>
                Знание требований нормативных правовых актов Российской Федерации в сфере образования, в том числе
                федеральных государственных образовательных стандартов
              </ColoredSmallText>
            </GridItem>
          )}
        </Col>
        {expert?.ecourses_experience && (
          <Col span={24}>
            <GridItem align="center" justify="flex-start" direction="row" wrap="nowrap" gap="10px">
              <CheckOutlined style={{ color: 'blue' }} />
              <ColoredSmallText>
                Опыт разработки или использования учебника(ов) или учебно-методических пособий в электронной форме,
                программ для электронной вычислительной машины или др. электронных образовательных ресурсов
              </ColoredSmallText>
            </GridItem>
          </Col>
        )}
        {expert?.expert_ecourses && (
          <Col span={24}>
            {expert?.expert_ecourses?.map((el, index) => {
              return (
                <GridItem key={index} align="center" justify="flex-start" direction="row" wrap="nowrap" gap="20px">
                  <ColoredSmallText key={index} color="rgba(56,72,91,.8)" size="14px">
                    {el?.name}
                  </ColoredSmallText>
                </GridItem>
              );
            })}
          </Col>
        )}
        {expert?.cartographic_experience && (
          <Col span={24}>
            <GridItem align="center" justify="flex-start" direction="row" wrap="nowrap" gap="10px">
              <CheckOutlined style={{ color: 'blue' }} />
              <ColoredSmallText>Опыт осуществления картографической деятельности не менее 5 лет</ColoredSmallText>
            </GridItem>
            <GridItem align="center" justify="flex-start" direction="row" wrap="nowrap" gap="10px">
              <ColoredSmallText color="rgba(56,72,91,.8)" size="14px">
                {expert?.cartographic_experience_experience}{' '}
                {declOfNum(expert?.cartographic_experience_experience, ['год', 'года', 'лет'])}
              </ColoredSmallText>
            </GridItem>
          </Col>
        )}

        {expert?.expert_cartographic_experience_organizations && (
          <Col span={24}>
            {expert?.expert_cartographic_experience_organizations?.map((el, index) => {
              return (
                <ColoredSmallText key={index} color="rgba(56,72,91,.8)" size="14px">
                  {el?.name}
                </ColoredSmallText>
              );
            })}
          </Col>
        )}
        {expert?.higher_education_correctional_pedagogy && (
          <Col span={24}>
            <GridItem align="center" justify="flex-start" direction="row" wrap="nowrap" gap="10px">
              <CheckOutlined style={{ color: 'blue' }} />
              <ColoredSmallText>
                Наличие высшего образования (квалификации) в области коррекционной педагогики
              </ColoredSmallText>
            </GridItem>
          </Col>
        )}

        {expert?.special_needs_qualification && (
          <Col span={24}>
            <GridItem align="center" justify="flex-start" direction="row" wrap="nowrap" gap="10px">
              <CheckOutlined style={{ color: 'blue' }} />
              <ColoredSmallText>Опыт работы в области коррекционной педагогики не менее 5 лет</ColoredSmallText>
            </GridItem>
            <ColoredSmallText color="rgba(56,72,91,.8)" size="14px">
              {expert?.special_needs_qualification_experience}{' '}
              {declOfNum(expert?.special_needs_qualification_experience, ['год', 'года', 'лет'])}
            </ColoredSmallText>
          </Col>
        )}

        {expert?.uses_school_textbook && (
          <Col span={24}>
            <GridItem align="center" justify="flex-start" direction="row" wrap="nowrap" gap="10px">
              <CheckOutlined style={{ color: 'blue' }} />
              <ColoredSmallText>Использование в своей деятельности учебников</ColoredSmallText>
            </GridItem>
          </Col>
        )}
        {expert?.expert_use_school_textbooks && (
          <Col span={24}>
            {expert?.expert_use_school_textbooks?.map((el, index) => {
              return (
                <ColoredSmallText key={index} color="rgba(56,72,91,.8)" size="14px">
                  {el?.name}
                </ColoredSmallText>
              );
            })}
          </Col>
        )}
        {expert?.is_edu_editions_author && (
          <Col span={24}>
            <GridItem align="center" justify="flex-start" direction="row" wrap="nowrap" gap="10px">
              <CheckOutlined style={{ color: 'blue' }} />
              <ColoredSmallText>Является автором учебных изданий</ColoredSmallText>
            </GridItem>
          </Col>
        )}
        {expert?.expert_edu_editions && (
          <Col span={24}>
            {expert?.expert_edu_editions?.map((el, index) => {
              return (
                <ColoredSmallText key={index} color="rgba(56,72,91,.8)" size="14px">
                  {el?.name}
                </ColoredSmallText>
              );
            })}
          </Col>
        )}
        {expert?.publishers_cooperation && (
          <Col span={24}>
            <GridItem align="center" justify="flex-start" direction="row" wrap="nowrap" gap="10px">
              <CheckOutlined style={{ color: 'blue' }} />
              <ColoredSmallText>Сотрудничает с издательствами</ColoredSmallText>
            </GridItem>
          </Col>
        )}
        {expert?.expert_publisher_cooperations && (
          <Col span={24}>
            {expert?.expert_publisher_cooperations?.map((el, index) => {
              return (
                <ColoredSmallText key={index} color="rgba(56,72,91,.8)" size="14px">
                  {publishersState?.find((e) => e.id === el?.publisher?.id)?.title}
                </ColoredSmallText>
              );
            })}
          </Col>
        )}
      </Row>
    </ExpertProfileContainer>
  );
};
